import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeName, ThemeSelection } from '../types/theme';
import { localStorageUtils, getSystemThemePreference } from '../utils/localStorage';
import { THEME_CONFIGS } from '../config/themes';

interface ThemeContextType {
  currentTheme: ThemeName;
  themeSelection: ThemeSelection;
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
  setLightTheme: (theme: ThemeName) => void;
  setDarkTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const DEFAULT_LIGHT_THEME: ThemeName = 'legacy';
const DEFAULT_DARK_THEME: ThemeName = 'deep-midnight';
const DEFAULT_MODE = 'light' as const;

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeSelection, setThemeSelection] = useState<ThemeSelection>({
    light: DEFAULT_LIGHT_THEME,
    dark: DEFAULT_DARK_THEME,
    currentMode: DEFAULT_MODE,
  });

  const [currentTheme, setCurrentTheme] = useState<ThemeName>(DEFAULT_LIGHT_THEME);

  useEffect(() => {
    // Load saved theme selection from localStorage
    const savedSelection = localStorageUtils.getItem('themeSelection') as ThemeSelection | null;
    
    if (savedSelection) {
      setThemeSelection(savedSelection);
      setCurrentTheme(savedSelection.currentMode === 'dark' ? savedSelection.dark : savedSelection.light);
      applyTheme(savedSelection.currentMode === 'dark' ? savedSelection.dark : savedSelection.light);
    } else {
      // Use system preference and defaults
      const systemPref = getSystemThemePreference();
      const initialSelection: ThemeSelection = {
        light: DEFAULT_LIGHT_THEME,
        dark: DEFAULT_DARK_THEME,
        currentMode: systemPref === 'dark' ? 'dark' : 'light',
      };
      const initialTheme = systemPref === 'dark' ? DEFAULT_DARK_THEME : DEFAULT_LIGHT_THEME;
      
      setThemeSelection(initialSelection);
      setCurrentTheme(initialTheme);
      applyTheme(initialTheme);
      localStorageUtils.setItem('themeSelection', initialSelection);
    }
  }, []);

  const applyTheme = (theme: ThemeName) => {
    const themeConfig = THEME_CONFIGS[theme];
    if (!themeConfig) return;

    const root = document.documentElement;
    root.setAttribute('data-theme', theme);

    // Apply CSS custom properties
    Object.entries(themeConfig.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Also set the mode for CSS hooks
    root.setAttribute('data-mode', themeConfig.mode);
  };

  const setTheme = (theme: ThemeName) => {
    const themeConfig = THEME_CONFIGS[theme];
    if (!themeConfig) return;

    const newSelection: ThemeSelection = {
      ...themeSelection,
      currentMode: themeConfig.mode,
    };

    if (themeConfig.mode === 'dark') {
      newSelection.dark = theme;
    } else {
      newSelection.light = theme;
    }

    setThemeSelection(newSelection);
    setCurrentTheme(theme);
    applyTheme(theme);
    localStorageUtils.setItem('themeSelection', newSelection);
  };

  const toggleTheme = () => {
    const newMode = themeSelection.currentMode === 'light' ? 'dark' : 'light';
    const themeToApply = newMode === 'dark' ? themeSelection.dark : themeSelection.light;
    
    const newSelection: ThemeSelection = {
      ...themeSelection,
      currentMode: newMode,
    };

    setThemeSelection(newSelection);
    setCurrentTheme(themeToApply);
    applyTheme(themeToApply);
    localStorageUtils.setItem('themeSelection', newSelection);
  };

  const setLightTheme = (theme: ThemeName) => {
    const newSelection: ThemeSelection = {
      ...themeSelection,
      light: theme,
    };

    // If currently in light mode, apply the new light theme
    if (themeSelection.currentMode === 'light') {
      setThemeSelection(newSelection);
      setCurrentTheme(theme);
      applyTheme(theme);
    } else {
      setThemeSelection(newSelection);
    }

    localStorageUtils.setItem('themeSelection', newSelection);
  };

  const setDarkTheme = (theme: ThemeName) => {
    const newSelection: ThemeSelection = {
      ...themeSelection,
      dark: theme,
    };

    // If currently in dark mode, apply the new dark theme
    if (themeSelection.currentMode === 'dark') {
      setThemeSelection(newSelection);
      setCurrentTheme(theme);
      applyTheme(theme);
    } else {
      setThemeSelection(newSelection);
    }

    localStorageUtils.setItem('themeSelection', newSelection);
  };

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        themeSelection,
        setTheme,
        toggleTheme,
        setLightTheme,
        setDarkTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
